import { useCallback } from 'react';
import { createFileRoute } from '@tanstack/react-router';

import type { SortDirection } from '~/api/types';
import type { PatientTableColumnKey } from '~/components/table/patient/types';
import { USER_ROLES } from '~/features/auth/roles';
import {
  PATIENTS_TABS,
  type PatientsTabsValue,
} from '~/features/customerAdministration/patients/constants';
import { PatientsSearchParamsProvider } from '~/features/customerAdministration/patients/params';
import { PatientsScreen } from '~/features/customerAdministration/patients/PatientsScreen';
import type {
  PatientsSearchParams,
  PatientsSearchParamsHandler,
} from '~/features/customerAdministration/patients/types';
import { protectedRoute } from '~/utils/routesAuthorization';

export const Route = createFileRoute('/customer-administration/patients')({
  component: RouteComponent,
  validateSearch: (search): PatientsSearchParams => {
    // Validate and parse the search params into a typed state
    const defaultSortDir: SortDirection =
      search.sortDir === 'asc' ? 'asc' : 'desc';
    const sortDir = search.sortDir ? defaultSortDir : undefined;
    const filters = search.filters ?? undefined;

    const tab =
      search.tab !== undefined &&
      Object.values(PATIENTS_TABS).includes(search.tab as PatientsTabsValue)
        ? (search.tab as PatientsTabsValue)
        : PATIENTS_TABS.searchAllPatients;

    return {
      page: search.page ? Number(search.page) || 1 : undefined,
      sort: (search.sort as PatientTableColumnKey | undefined) ?? undefined,
      search:
        search.search && typeof search.search === 'string'
          ? decodeURI(search.search)
          : undefined,
      filters,
      sortDir,
      tab,
    };
  },
  beforeLoad: () => {
    protectedRoute({
      roles: [USER_ROLES.AdminAbbott, USER_ROLES.AbbottUser],
    });
  },
});

function RouteComponent() {
  const navigate = Route.useNavigate();

  const onSearchParamsChange: PatientsSearchParamsHandler = useCallback(
    (onSearch) => navigate({ search: onSearch }),
    [navigate],
  );

  return (
    <PatientsSearchParamsProvider
      params={Route.useSearch()}
      onSearchParamsChange={onSearchParamsChange}
    >
      <PatientsScreen />
    </PatientsSearchParamsProvider>
  );
}
